import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../components/Layout";
import Container from "../../../components/Container";
import Flex from "../../../components/Flex";
import Content from "../../../components/Content";
import Button from "../../../components/Button";
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../../../components/DataTable";
import { useAuthState, requiresClient } from "../../../components/AuthProvider";
import dateTimeDiff from "../dateTimeDiff";
import FlickerText from "../../../components/FlickerText";
import ButtonList from "../../../components/ButtonList";
import LinkButton from "../../../components/LinkButton";
import task_types_dict from "../taskTypes";
import Spinner from "../../../components/Loader/Spinner";

import styles from "./ViewTask.module.scss";
import { toast } from "react-toastify";

const ViewTask = () => {
  const moment = require("moment-timezone");
  // TODO this functionality has been unused and is deprecated
  const clientConfigApiV1Domain = process.env.REACT_APP_CLIENT_CONFIG_API_V1_DOMAIN;
  const dataLoadRpcUrl = `${clientConfigApiV1Domain}/rpc/batchloads`;
  const downloadApiUrl = `${clientConfigApiV1Domain}/download`;
  const { taskId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();

  const [dataLoadData, setDataLoadData] = useState([]);
  const [dataLoadEMRResultsData, setDataLoadEMRResultsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const parseResults = data => {
      if (data.result.hasOwnProperty("emr_step_results")) {
        const cleaned_data = data.result.emr_step_results
          .replace(/'/g, `"`)
          .replace(/datetime.datetime\(/g, `"`)
          .replace(/, tzinfo=tzlocal\(\)\)/g, `"`);

        setDataLoadEMRResultsData(JSON.parse(cleaned_data));
      }
    };
    const fetchDataLoadData = async () => {
      const response = await fetch(`${dataLoadRpcUrl}/${taskId}/`);
      const data = await response.json();
      setDataLoadData(data);
      parseResults(data);
    };

    if (!hasLoaded) {
      setIsLoading(true);
      fetchDataLoadData().then(() => {
        setIsLoading(false);
        setHasLoaded(true);
      });
    }
  }, [
    setIsLoading,
    setHasLoaded,
    hasLoaded,
    taskId,
    dataLoadRpcUrl,
    downloadApiUrl
  ]);

  useEffect(() => {
    if (hasLoaded) {
      toast.success("Client changed. Redirected to Tasks page.");
      history.push(`/tasks`);
    }
    // do not add history and hasLoaded as this will create redirect to task page again!
  }, [user.preferences.selectedClient]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = "Loading ...";
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item render={() => <Link to="/tasks">Tasks</Link>} />
        <Breadcrumbs.Item last render={() => `Tasks: ${taskId}`} />
      </Breadcrumbs>
    );
  }

  function formattedTimeDiff(to, from) {
    const toSplit = to.split(", ");
    const fromSplit = from.split(", ");
    //new Date(1995, 11, 17, 3, 24, 0)
    return dateTimeDiff(
      new Date(
        toSplit[0],
        toSplit[1],
        toSplit[2],
        toSplit[3],
        toSplit[4],
        toSplit[5]
      ),
      new Date(
        fromSplit[0],
        fromSplit[1],
        fromSplit[2],
        fromSplit[3],
        fromSplit[4],
        fromSplit[5]
      )
    );
  }

  const columns = [
    {
      Header: "ID",
      accessor: "Id",
      canSort: false
    },
    {
      Header: "Name",
      accessor: "Name",
      canSort: false
    },
    {
      Header: "Runtime",
      accessor: row =>
        formattedTimeDiff(
          row.Status.Timeline.EndDateTime,
          row.Status.Timeline.StartDateTime
        )
    },
    {
      Header: "Status",
      accessor: "Status.State",
      canSort: false
    }
  ];

  const fetchFileData = async url => {
    const response = await fetch(url);
    const blob = await response.blob();
    return blob;
  };

  const createDownload = (file_blob, file) => {
    const download_url = window.URL.createObjectURL(new Blob([file_blob]));
    const link = document.createElement("a");
    link.href = download_url;
    link.setAttribute("download", taskId + file);

    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const patientMatchVectorOnClick = () => {
    const url = `${downloadApiUrl}/?task_id=${taskId}&file=PatientMatchVector`;
    fetchFileData(url).then(blob => {
      createDownload(blob, `-matchVectors.csv`);
    });
  };

  const matchModelRuleNotationOnClick = () => {
    const url = `${downloadApiUrl}/?task_id=${taskId}&file=MatchModelRuleNotation`;
    fetchFileData(url).then(blob => {
      createDownload(blob, `-rules.csv`);
    });
  };

  const expectationMaximizationOnClick = () => {
    const url = `${downloadApiUrl}/?task_id=${taskId}&file=ExpectationMaximization`;
    fetchFileData(url).then(blob => {
      createDownload(blob, `-expectationMaximization.csv`);
    });
  };

  const entityChangeNotificationOnClick = () => {
    const url = `${downloadApiUrl}/?task_id=${taskId}&file=PatientChangeSummary`;
    fetchFileData(url).then(blob => {
      createDownload(blob, `-entityChangeNotificationSummary.csv`);
    });
  };

  const dataQualityIssuesOnClick = () => {
    const url = `${downloadApiUrl}/?task_id=${taskId}&file=DataQualityIssues`;
    fetchFileData(url).then(blob => {
      createDownload(blob, `-dataQualityIssues.csv`);
    });
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>Task: {taskId}</h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Task Details</h3>
                  <div>
                    <Button
                      className={styles.margin}
                      primary
                      onClick={() => {
                        history.push(`${taskId}/rerun`);
                      }}
                    >
                      Rerun
                    </Button>
                  </div>
                </div>
                <div className={styles.details}>
                  <div>
                    <h4>ID</h4>
                    <span>{taskId}</span>
                  </div>
                  <div>
                    <h4>Type</h4>
                    <span>
                      {task_types_dict.hasOwnProperty(dataLoadData.task_name)
                        ? task_types_dict[dataLoadData.task_name]
                        : dataLoadData.task_name}
                    </span>
                  </div>

                  <div>
                    <h4>Received At</h4>
                    <span>
                      {dataLoadData &
                      (dataLoadData.date_received === "NOT_AVAILABLE")
                        ? dataLoadData.date_received
                        : moment(dataLoadData.date_received.replace(" ", "T"))
                            .tz(user.preferences.timeZone)
                            .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Status</h4>
                    <span>
                      {dataLoadData.status === "SUCCESSFUL" ||
                      dataLoadData.status === "ERROR" ||
                      dataLoadData.status === "FAILED" ? (
                        dataLoadData.status
                      ) : (
                        <FlickerText text={dataLoadData.status} />
                      )}
                    </span>
                  </div>
                  <div>
                    <h4>Started At</h4>
                    <span>
                      {dataLoadData &
                      (dataLoadData.date_started !== "NOT_AVAILABLE")
                        ? dataLoadData.date_started
                        : moment(dataLoadData.date_started.replace(" ", "T"))
                            .tz(user.preferences.timeZone)
                            .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Finished At</h4>
                    <span>
                      {dataLoadData &
                      (dataLoadData.date_done !== "NOT_AVAILABLE")
                        ? dataLoadData.date_done
                        : moment(dataLoadData.date_done.replace(" ", "T"))
                            .tz(user.preferences.timeZone)
                            .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Input Arguments</h4>
                    <pre
                      style={{
                        background: "#f6f8fa",
                        padding: ".5rem"
                      }}
                    >
                      {JSON.stringify(dataLoadData.task_kwargs, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <h4>EMR Id</h4>
                    <span>{dataLoadData.worker}</span>
                  </div>

                  <div className={styles.detailsFlex}>
                    <h3>Run Results</h3>
                  </div>
                  <div>
                    <p>
                      The total runtime below does not include the time required
                      to startup and terminate the machines used to run this
                      task.
                    </p>
                    <DataTable
                      columns={columns}
                      data={dataLoadEMRResultsData}
                      hasPagination
                    />
                  </div>

                  {dataLoadData.task_name === "matcher" && (
                    <>
                      <div className={styles.detailsFlex}>
                        <h3>Output Download</h3>
                      </div>

                      {dataLoadData.status !== "SUCCESSFUL" ||
                      dataLoadData.status !== "ERROR" ||
                      dataLoadData.status !== "FAILED" ? (
                        dataLoadData.status === "SUCCESSFUL" ? (
                          <>
                            <ButtonList
                              spacing={2}
                              style={{ margin: "1rem 0" }}
                            >
                              <LinkButton
                                icon="download"
                                onClick={dataQualityIssuesOnClick}
                              >
                                Data Quality Issues
                              </LinkButton>

                              {parseFloat(
                                dataLoadData.task_kwargs.near_miss_threshold
                              ) > 0 && (
                                <LinkButton
                                  icon="download"
                                  onClick={patientMatchVectorOnClick}
                                >
                                  Patient Match Vector
                                </LinkButton>
                              )}
                              {parseFloat(
                                dataLoadData.task_kwargs.near_miss_threshold
                              ) > 0 && (
                                <LinkButton
                                  icon="download"
                                  onClick={matchModelRuleNotationOnClick}
                                >
                                  Match Model Rule Notation
                                </LinkButton>
                              )}
                            </ButtonList>
                            <ButtonList
                              spacing={2}
                              style={{ margin: "1rem 0" }}
                            >
                              {parseFloat(
                                dataLoadData.task_kwargs.near_miss_threshold
                              ) > 0 && (
                                <LinkButton
                                  icon="download"
                                  onClick={expectationMaximizationOnClick}
                                >
                                  Expectation Maximization
                                </LinkButton>
                              )}
                              {parseFloat(
                                dataLoadData.task_kwargs.near_miss_threshold
                              ) > 0 && (
                                <LinkButton
                                  icon="download"
                                  onClick={entityChangeNotificationOnClick}
                                >
                                  Entity Change Report Summary
                                </LinkButton>
                              )}
                            </ButtonList>
                          </>
                        ) : (
                          <div>
                            <p>Failed Task has no download links generated.</p>
                          </div>
                        )
                      ) : (
                        <div>
                          <p>
                            Task is running. Please check later for links to
                            download output files.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default requiresClient(ViewTask);
